import { useMemo } from 'react';
import { Box, Typography, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { descriptiveStats, extractValues } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

const STAT_ROWS = [
  { key: 'min', label: 'Min' },
  { key: 'max', label: 'Max' },
  { key: 'delta', label: 'Delta' },
  { key: 'mean', label: 'Mean' },
  { key: 'stdev', label: 'Std Dev' },
  { key: 'q1', label: 'Q1' },
  { key: 'median', label: 'Median' },
  { key: 'q3', label: 'Q3' },
  { key: 'iqr', label: 'IQR' },
  { key: 'mode', label: 'Mode' },
  { key: 'count', label: 'Count' },
];

export default function ComparisonWidget({ recordings = [], recordingMetadata = {}, widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId1 = widgetConfig?.recId1 || '';
  const recId2 = widgetConfig?.recId2 || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec1 = recordings.find((r) => r.id === recId1);
  const rec2 = recordings.find((r) => r.id === recId2);

  const stats1 = useMemo(() => {
    if (!rec1 || !sensorKey) return null;
    return descriptiveStats(extractValues(rec1, sensorKey, timeWindow?.start, timeWindow?.end));
  }, [rec1, sensorKey, timeWindow]);

  const stats2 = useMemo(() => {
    if (!rec2 || !sensorKey) return null;
    return descriptiveStats(extractValues(rec2, sensorKey, timeWindow?.start, timeWindow?.end));
  }, [rec2, sensorKey, timeWindow]);

  const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    return typeof v === 'number' ? v.toFixed(2) : String(v);
  };

  const diffColor = (diff) => {
    if (diff === null || diff === undefined || isNaN(diff)) return 'text.secondary';
    return diff > 0 ? 'error.main' : diff < 0 ? 'success.main' : 'text.secondary';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Results table */}
      {stats1 && stats2 ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Stat</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{(rec1 && recordingMetadata[rec1.id]?.label) || rec1?.name || 'Rec 1'}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{(rec2 && recordingMetadata[rec2.id]?.label) || rec2?.name || 'Rec 2'}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Diff</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {STAT_ROWS.map(({ key, label }) => {
                const v1 = stats1[key];
                const v2 = stats2[key];
                const diff = typeof v1 === 'number' && typeof v2 === 'number' ? v2 - v1 : null;
                return (
                  <TableRow key={key}>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>{label}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{fmt(v1)}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{fmt(v2)}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: diffColor(diff) }} align="right">
                      {diff !== null ? (diff > 0 ? '+' : '') + diff.toFixed(2) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select two recordings and a sensor to compare
          </Typography>
        </Box>
      )}
    </Box>
  );
}
